import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
	EnvelopeIcon,
	PhoneIcon,
	MapPinIcon,
	BellIcon,
	BellSlashIcon,
	TrashIcon,
} from '@heroicons/react/24/outline';

import { getPowerUser, deletePowerUser, toggleNotifications } from '../../api/power-users';
import { getUserFullName } from '../../components/helpers/functions';
import { MainContent, PageHeading } from '../../components/Shell/Content';
import { AvatarGenerated } from '../../components/Avatar';
import LoadingSkeleton from '../../components/Shell/LoadingSkeleton';
import EllipsisDropdown, {
	EllipsisDropdownItem,
} from '../../components/EllipsisDropdown';
import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';
import useSettings from '../../hooks/useSettings';
import { parseAssetTemplate } from '../../components/nextgen/AssetSelectorBeta';

const PowerUserProfile = () => {
	const navigate = useNavigate();
	const { id } = useParams();

	const {
		value: powerUser,
		execute: refreshPowerUser,
		status,
	} = useAsync(getPowerUser, { immediate: false });

	const { execute: onDeletePowerUser } = useAsync(deletePowerUser, {
		immediate: false,
		showNotification: { success: 'Power user deleted' },
	});

	const { execute: onToggleNotifications, status: togglingNotifications } = useAsync(
		toggleNotifications,
		{
			immediate: false,
			showNotification: { success: 'Notifications updated' },
		}
	);

	const { setOpen: setDeleteModalOpen, setClose: setDeleteModalClose } =
		useModal('deleteModal');

	const settings = useSettings();
	const assetDisplayTemplate = settings.get('assetDisplay');

	useEffect(() => {
		if (id) {
			refreshPowerUser({ id });
		}
	}, [id, refreshPowerUser]);

	const handleToggleNotifications = () => {
		onToggleNotifications(
			{
				id: powerUser.id,
				notifications: !powerUser.notifications,
			},
			() => refreshPowerUser({ id })
		);
	};

	const getDisplayName = () => {
		if (!powerUser) return '';
		return getUserFullName(powerUser) || powerUser.email || 'Unnamed Power User';
	};

	return (
		<MainContent maxWidth="w-full bg-gray-50 min-h-screen">
			{status === 'pending' && !powerUser ? (
				<div className="max-w-5xl mx-auto">
					<LoadingSkeleton />
				</div>
			) : !powerUser ? (
				<div className="text-center py-12">
					<p className="text-sm text-gray-500">Power user not found</p>
				</div>
			) : (
				<div className="max-w-5xl mx-auto">
					{/* Header */}
					<PageHeading
						actions={[
							{
								render: () => (
									<EllipsisDropdown>
										<EllipsisDropdownItem
											danger
											onClick={() =>
												setDeleteModalOpen({
													title:
														'Are you sure you want to delete this power user?',
													description:
														'This action cannot be undone. The power user will no longer have access to assigned assets.',
													confirmText: 'Delete Power User',
													onConfirm: () =>
														onDeletePowerUser(powerUser?.id, () => {
															setDeleteModalClose();
															navigate('/manage/team/power-users');
														}),
												})
											}
										>
											<div className="flex items-center">
												<TrashIcon className="mr-3 h-5 w-5" />
												Delete Power User
											</div>
										</EllipsisDropdownItem>
									</EllipsisDropdown>
								),
							},
						]}
						divider={false}
					>
						<button
							onClick={() => navigate('/manage/team/power-users')}
							className="text-sm text-gray-500 hover:text-gray-700 font-normal"
						>
							← Back to Power Users
						</button>
					</PageHeading>

					<div className="">
						<div className="grid grid-cols-5 gap-x-6">
							{/* Left Sidebar - Contact Info */}
							<div className="col-span-2">
								<div className="bg-white border-gray-200 border rounded-lg mt-6">
									<div className="flex items-center gap-3 py-4 px-6 border-b border-gray-200">
										<AvatarGenerated size="sm" id={getDisplayName()} />
										<h3 className="text-sm font-medium text-gray-900">
											{getDisplayName()}
										</h3>
									</div>

									<dl className="grid grid-cols-1 gap-y-4 px-6 py-6">
										<div className="flex items-start gap-3">
											<EnvelopeIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
											<div className="min-w-0 flex-1">
												<dt className="text-xs font-medium text-gray-500">
													Email
												</dt>
												<dd className="mt-1 text-sm text-gray-900 break-words">
													{powerUser.email ? (
														<a
															href={`mailto:${powerUser.email}`}
															className="text-indigo-600 hover:text-indigo-700"
														>
															{powerUser.email}
														</a>
													) : (
														<span className="text-gray-400">—</span>
													)}
												</dd>
											</div>
										</div>

										<div className="flex items-start gap-3">
											<PhoneIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
											<div className="min-w-0 flex-1">
												<dt className="text-xs font-medium text-gray-500">
													Phone
												</dt>
												<dd className="mt-1 text-sm text-gray-900">
													{powerUser.phoneNumber ? (
														<a
															href={`tel:${powerUser.phoneNumber}`}
															className="text-indigo-600 hover:text-indigo-700"
														>
															{powerUser.phoneNumber}
														</a>
													) : (
														<span className="text-gray-400">—</span>
													)}
												</dd>
											</div>
										</div>

										<div className="flex items-start gap-3">
											<MapPinIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
											<div className="min-w-0 flex-1">
												<dt className="text-xs font-medium text-gray-500">
													Location
												</dt>
												<dd className="mt-1 text-sm text-gray-900">
													{powerUser.location || (
														<span className="text-gray-400">—</span>
													)}
												</dd>
											</div>
										</div>
									</dl>
								</div>

								{/* Notifications Toggle */}
								<div className="bg-white border-gray-200 border rounded-lg mt-6">
									<div className="py-4 px-6 border-b border-gray-200">
										<h3 className="text-sm font-medium text-gray-900 flex items-center">
											Notifications
										</h3>
									</div>
									<div className="px-6 py-4">
										<div className="flex items-center justify-between">
											<div className="flex items-center gap-3">
												{powerUser.notifications ? (
													<BellIcon className="w-5 h-5 text-indigo-600" />
												) : (
													<BellSlashIcon className="w-5 h-5 text-gray-400" />
												)}
												<div>
													<p className="text-sm font-medium text-gray-900">
														Email Notifications
													</p>
													<p className="text-xs text-gray-500">
														{powerUser.notifications
															? 'Receiving notifications for assigned assets'
															: 'Notifications are disabled'}
													</p>
												</div>
											</div>
											<button
												type="button"
												onClick={handleToggleNotifications}
												disabled={togglingNotifications === 'pending'}
												className={`
													relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
													transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
													${powerUser.notifications ? 'bg-indigo-600' : 'bg-gray-200'}
													${togglingNotifications === 'pending' ? 'opacity-50 cursor-not-allowed' : ''}
												`}
											>
												<span
													className={`
														pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
														transition duration-200 ease-in-out
														${powerUser.notifications ? 'translate-x-5' : 'translate-x-0'}
													`}
												/>
											</button>
										</div>
									</div>
								</div>
							</div>

							{/* Right Sidebar - Assets */}
							<div className="col-span-3">
								<div className="bg-white rounded-lg border border-gray-200 mt-6">
									<div className="flex items-center justify-between py-4 px-6 border-b border-gray-200">
										<h3 className="text-sm font-medium text-gray-900 flex items-center">
											Assigned Assets{' '}
											{powerUser.assets &&
												powerUser.assets.length > 0 &&
												`(${powerUser.assets.length})`}
										</h3>
									</div>
									{powerUser.assets && powerUser.assets.length > 0 ? (
										<ul className="divide-y divide-gray-200 m-0 p-0 !mt-0">
											{powerUser.assets.map((asset) => (
												<li
													key={asset.id}
													className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
													onClick={() =>
														navigate(`/assets/${asset.id}/overview`)
													}
												>
													<div className="flex items-center justify-between">
														<div className="min-w-0 flex-1">
															<p className="text-sm font-medium text-gray-900 truncate">
																{[
																	asset.manufacturer?.name ||
																		asset.manufacturerName,
																	asset.model?.name || asset.modelName,
																]
																	.filter(Boolean)
																	.join(' ') || 'Unnamed Asset'}
															</p>
															<p className="text-xs text-gray-500 truncate">
																{assetDisplayTemplate
																	? parseAssetTemplate(
																			assetDisplayTemplate,
																			asset,
																		)
																	: `${asset.location?.name || 'No location'} · SN: ${asset.serialNumber || 'N/A'}`}
															</p>
														</div>
													</div>
												</li>
											))}
										</ul>
									) : (
										<div className="px-6 py-8 text-center">
											<p className="text-sm text-gray-500">No assets assigned</p>
											<p className="text-xs text-gray-400 mt-1">
												Assign this power user to assets to give them access
											</p>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</MainContent>
	);
};

export default PowerUserProfile;
